import React from 'react'

import {
  anyPass,
  path as fromPath,
  pipe,
  prop,
  propIs,
} from 'ramda'
import { createRouteBundle, createSelector } from 'redux-bundler'

import { camelize, singularize } from 'inflection'
import queryString from 'query-string'

import createLogger from '~/src/Lib/Logging'
import { debounce, defer, EMPTY_OBJECT } from '~/src/Lib/Utils'
import { ENTITY_ALTERNATE_LOOKUPS } from '~/src/Store/bundles/entities'
import { REACTOR_PRIORITIES } from '~/src/Store/constants'

import routes, { routeLookupHandler, SCHEMA_ACTION_TO_ROUTE } from './routes'

export const OPEN_DIALOG = 'OPEN_DIALOG'
export const CLOSE_DIALOG = 'CLOSE_DIALOG'
export const SET_DIALOG_ENTITY = 'SET_DIALOG_ENTITY'
export const CLEAR_DIALOG_ENTITY = 'CLEAR_DIALOG_ENTITY'

const logger = createLogger('Dialog/bundle')

const defaultState = Object.freeze({ open: 0, entity: null })
const debouncedDoOpen = debounce(({ dispatch }) => dispatch({ type: OPEN_DIALOG }), 150, true)
const debouncedDoClose = debounce(({ dispatch }) => dispatch({ type: CLOSE_DIALOG }), 150, true)
const debouncedClearEntity = debounce(({ dispatch }) => dispatch({ type: CLEAR_DIALOG_ENTITY }), 300, true)
const getUUID = ({ schema, id }, opts) => [schema ?? opts?.defaultSchema, id].filter(Boolean).join('_')
const isPathRoute = anyPass([propIs(Array, 'path'), propIs(String, 'schema'), propIs(String, 'action')])
const getRouteConfig = path => fromPath(path, SCHEMA_ACTION_TO_ROUTE)

const routingBundle = Object.entries(createRouteBundle(routes, { routeInfoSelector: 'selectHash' }))
  .reduce((acc, [key, value]) => (key.startsWith('select') ? {
    ...acc,
    [key.replace('select', 'selectDialog')]: value
  } : acc), {})

export const mainBundle = {
  name: 'dialog',
  reducer: (state = defaultState, action = {}) => {
    switch (action.type) {
      case OPEN_DIALOG:
        return { ...state, open: state.open + 1 }
      case CLOSE_DIALOG:
        return { ...state, open: Math.max(0, state.open - 1) }
      case SET_DIALOG_ENTITY:
        return { ...state, entity: action.payload }
      case CLEAR_DIALOG_ENTITY:
        return { ...state, entity: defaultState.entity }
      default:
        return state
    }
  },
  selectDialog: prop('dialog'),
  selectDialogIsOpen: createSelector('selectDialog', pipe(prop('open'), Boolean)),
  doOpenDialog: () => debouncedDoOpen,
  doCloseDialog: () => debouncedDoClose,
}

export default {
  ...routingBundle,
  ...mainBundle,
  selectDialogRouteParams: createSelector('selectDialogRouteInfo', prop('params')),
  selectDialogQueryObject: createSelector('selectDialogRouteInfo', ({ url }) => {
    if (url && url.match(/\?\w+/)) {
      return queryString.parse(url.slice(url.indexOf('?')))
    }
    return EMPTY_OBJECT
  }),
  selectDialogRoute: createSelector('selectDialogRouteInfo', ({ value, params }) => {
    logger.debug('[selectDialogRoute] initial:', { params, value })

    if (isPathRoute(value)) {
      const { action, path: routePath, schema } = value
      const path = routePath ?? [schema, action].filter(Boolean)
      const config = getRouteConfig(path)
      const handler = routeLookupHandler({
        path,
        ...config,
      })
      logger.debug('[selectDialogRoute] resolved route from value:', { ...handler })
      return handler
    }

    if (isPathRoute(params)) {
      const { action: paramsAction, id, schema } = params
      const action = paramsAction || (id && !id.match(/\d+/) ? id : null) || value.defaultAction || 'view'
      const config = getRouteConfig([schema, action])

      if (config && typeof config === 'object' && !config.$$typeof) {
        const handler = routeLookupHandler({
          path: [schema, action],
          ...config,
        })
        logger.debug('[selectDialogRoute] resolved route from params:', { ...handler }, React.isValidElement(config))
        return handler
      }
    }

    logger.debug('[selectDialogRoute] default route:', { ...value, params })
    return value
  }),
  selectDialogEntity: createSelector('selectDialog', prop('entity')),
  doCloseDialogRoute: () => ({ store, dispatch }) => {
    const {
      dialogEntity,
      dialogRouteInfo
    } = store.select(['selectDialogEntity', 'selectDialogRouteInfo'])
    dispatch({ actionCreator: 'doUpdateHash', args: ['', { maintainScrollPosition: true }] })
    const [schema] = dialogEntity?.split('_') ?? [(dialogRouteInfo?.params?.schema ?? '')]
    if (!schema || !schema.trim()) return
    const isNew = Boolean(dialogRouteInfo?.url?.endsWith('new'))
    defer(() => {
      const {
        dialogEntity: newEntity,
        dialogRouteInfo: newRouteInfo
      } = store.select(['selectDialogEntity', 'selectDialogRouteInfo'])
      const uuid = newRouteInfo?.params ? getUUID(newRouteInfo?.params) : null
      if (isNew || (newEntity && uuid === newEntity)) {
        const actionName = `do${camelize(singularize(schema))}SetCurrent`
        dispatch({
          actionCreator: actionName,
          args: [previous => (isNew || (uuid && uuid.endsWith(previous)) ? null : previous)]
        })
        dispatch({ type: CLEAR_DIALOG_ENTITY })
      }
    }, defer.priorities.low)
  },
  doClearDialogEntity: () => debouncedClearEntity,
  doSetDialogEntity: payload => ({ store, dispatch }) => {
    dispatch({ type: SET_DIALOG_ENTITY, payload })
    const [schema, idString] = payload.split('_')
    if (!schema.trim() || !idString.trim()) {
      // if we don't have a schema and an id, we can't do anything
      return
    }
    const id = Number(idString)
    const entitySingular = camelize(singularize(
      schema in ENTITY_ALTERNATE_LOOKUPS ? ENTITY_ALTERNATE_LOOKUPS[schema] : schema
    ))
    const inflightsName = `${schema}Inflight`
    const inflightsSelector = `select${camelize(schema)}Inflight`
    const { currents, dialogRoute, entities, [inflightsName]: inflights = EMPTY_OBJECT } = store.select([
      'selectCurrents',
      'selectDialogRoute',
      'selectEntities',
      inflightsSelector in store ? inflightsSelector : null
    ].filter(Boolean))
    if (!id) {
      return
    }
    const setActionName = `do${entitySingular}SetCurrent`
    if (currents[schema] !== id && setActionName in store) {
      dispatch({ actionCreator: setActionName, args: [id] })
    }
    const shouldAutoFetch = dialogRoute?.autoFetch !== false
    const notAlreadyFetched = !entities[payload] || entities[payload].payloadType === 'summary'
    const notInflight = !inflights[id]
    if (shouldAutoFetch && notAlreadyFetched && notInflight) {
      const fetchActionName = typeof dialogRoute.autoFetch === 'string'
        ? dialogRoute.autoFetch
        : `do${entitySingular}Fetch`
      if (fetchActionName === 'doHarvestFetch') {
        logger.debug('doSetDialogEntity', { fetchActionName, id })
      }
      if (fetchActionName in store) {
        dispatch({ actionCreator: fetchActionName, args: [id] })
      }
    }
  },
  reactSetCurrentEntity: createSelector(
    'selectDialogRouteInfo',
    'selectDialogRoute',
    'selectDialogEntity',
    'selectCurrents',
    (info, dialog, currentUUID, currents) => {
      if (info && info.params) {
        const { params = EMPTY_OBJECT } = info
        const { action: paramsAction, id: paramsId, schema: paramsSchema } = params
        const schema = paramsSchema || dialog.schema || dialog.defaultSchema
        const action = paramsAction ?? dialog.defaultAction ?? (paramsId && !paramsId.match(/\d+/) ? paramsId : '')
        logger.debug('[reactSetCurrentEntity] massaged params:', {
          params: { schema, action },
          routeInfo: info,
          dialog,
        })
        if (schema && action) {
          const config = getRouteConfig([schema, action])
          logger.debug('[reactSetCurrentEntity] massaged route:', { config, dialog: { ...dialog } })
          if (config?.autoFetch === false) {
            return undefined
          }
        }
        if (params.id && schema) {
          const nextUUID = getUUID(params, dialog)
          if (typeof nextUUID !== 'string') return undefined
          return nextUUID !== currentUUID || !currents[schema] || !nextUUID.endsWith(currents[schema])
            ? { actionCreator: 'doSetDialogEntity', args: [nextUUID] }
            : undefined
        }
        if (!params.id && currents[schema] != null) {
          return {
            actionCreator: `do${camelize(singularize(schema))}SetCurrent`,
            args: [null],
            priority: REACTOR_PRIORITIES.HIGH
          }
        }
      }
      if (currentUUID && !info?.params) {
        return { actionCreator: 'doClearDialogEntity' }
      }

      return undefined
    }
  ),
}
